// Reposition (it was "Crop & position"): the author drags an image to move it inside its box and
// zooms with the slider, the wheel, a pinch or +/−, and gets back an
// ImageFrame (lib/frame.ts) to store. Full-width, inline and wrapped images
// are cropped freely instead: a crop box with corner and edge handles over the
// whole photo (CropBox), turned into a frame whose box takes the crop's shape.
// An inline image's crop box starts on the band it shows before cropping. Nothing touches the file, so any framing
// can be redone or reset. The composer opens it for the cover, image blocks
// and scenes; `target` says which box the image fills, and so the stage's
// shape and the previews beside it.
//
// The parent closes it: onDone(frame) is the end of the flow (null = the
// default framing, store nothing), and onClose() only means "cancelled"
// (Cancel, Escape, the scrim).

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from 'react'
import type { ImageFrame } from '../lib/types'
import {
  cropToFrame,
  frameToCrop,
  FULL_CROP,
  INLINE_BOX,
  INLINE_REST_Y,
  MAX_ZOOM,
  MIN_ZOOM,
  panBy,
  safeFrame,
  zoomTo,
  type Box,
  type CropRect,
} from '../lib/frame'
import { useMediaQuery } from '../lib/hooks'
import { FramedImage } from './FramedImage'
import { CropBox } from './CropBox'
import { btn, labelCls, MobileSheet } from './ui'

/** The box an image is framed for. */
export type FrameTarget = 'cover' | 'inline' | 'wrap' | 'full' | 'scene-half' | 'scene-full'

export interface ImageEditorProps {
  open: boolean
  src: string
  /** The stored frame; null/undefined = the default framing. */
  frame: ImageFrame | null | undefined
  target: FrameTarget
  onClose(): void
  onDone(frame: ImageFrame | null): void
}

interface Preview {
  label: string
  /** Width and height in px where it shows (only the ratio is used). */
  w: number
  h: number
}

interface TargetSpec {
  label: string
  /** The stage's width / height; null = the frame's own shape (a full-width image). */
  aspect: number | null
  /**
   * Where an unframed image sits vertically (CSS object-position), so opening
   * the editor never moves it: inline and wrapped images sit at 'center 60%'.
   */
  restY: number
  scene: boolean
  previews: Preview[]
}

const PHONE_COLUMN = 358 // a 390px phone less the column's 16px gutters

// The same boxes the composer and the reader draw (ComposerHeader, BlockViews,
// SceneView; ArticleView, reader/ArticleBody, ReaderPage's "More from").
const TARGETS: Record<FrameTarget, TargetSpec> = {
  cover: {
    label: 'COVER',
    aspect: 816 / 320,
    restY: 0.5,
    scene: false,
    previews: [
      { label: 'Desktop', w: 816, h: 320 },
      { label: 'Phone', w: PHONE_COLUMN, h: 220 },
      { label: '“More from” card', w: 200, h: 110 },
    ],
  },
  // Cropped freely (CropBox): the crop is the shape everywhere, so no previews.
  inline: { label: 'INLINE IMAGE', aspect: null, restY: INLINE_REST_Y, scene: false, previews: [] },
  // A wrap floats at its photo's own shape (Composer lab's wrap), so it's cropped freely too.
  wrap: { label: 'WRAPPED IMAGE', aspect: null, restY: 0.6, scene: false, previews: [] },
  // The box takes the chosen shape everywhere, so there's nothing else to preview.
  full: { label: 'FULL-WIDTH IMAGE', aspect: null, restY: 0.5, scene: false, previews: [] },
  'scene-half': {
    label: 'SCENE',
    aspect: 1272 / 360,
    restY: 0.5,
    scene: true,
    previews: [
      { label: 'Desktop', w: 1272, h: 360 },
      { label: 'Phone', w: 390, h: 300 },
    ],
  },
  'scene-full': {
    label: 'FULL-HEIGHT SCENE',
    aspect: 16 / 9,
    restY: 0.5,
    scene: true,
    previews: [
      { label: 'Desktop', w: 16, h: 9 },
      { label: 'Phone', w: 9, h: 16 },
    ],
  },
}

const STAGE_MAX_W = 720
/** Room the dialog's other parts need, so the stage leaves them on screen. */
const STAGE_MAX_H = { desktop: 'max(200px, calc(100dvh - 430px))', phone: 'max(170px, calc(82dvh - 350px))' }
/** Previews fit a box this size. */
const PREVIEW_BOX = { desktop: { w: 150, h: 80 }, phone: { w: 116, h: 64 } }
/** One keyboard / button zoom step. */
const ZOOM_STEP = 1.12

/** The scene's wash, darkest in the middle where its line sits (as lib/sceneColors' SCENE_SHADE on writings-fb-scenes). */
const SCENE_SHADE = 'bg-[radial-gradient(ellipse_at_center,rgba(45,27,30,.58),rgba(45,27,30,.3)_70%,rgba(45,27,30,.22))]'

interface Point {
  x: number
  y: number
}

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n))
const near = (a: number, b: number) => Math.abs(a - b) < 5e-4
const round = (n: number, dp: number) => Number(n.toFixed(dp))
const distance = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y)
const midpoint = (a: Point, b: Point): Point => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 })

/** zoomTo, but a box that hasn't laid out yet just takes the zoom (no anchor maths on 0×0). */
function zoomIn(f: ImageFrame, box: Box, zoom: number, anchor?: Point): ImageFrame {
  if (box.width > 0 && box.height > 0) return zoomTo(f, box, zoom, anchor)
  return { ...f, zoom: clamp(zoom, MIN_ZOOM, MAX_ZOOM) }
}

/** Short numbers for storage: nothing the eye could tell apart is lost. */
function tidy(f: ImageFrame): ImageFrame {
  return {
    natural: round(f.natural, 4),
    focus: { x: round(f.focus.x, 4), y: round(f.focus.y, 4) },
    zoom: round(f.zoom, 3),
    ...(f.shape !== undefined ? { shape: round(f.shape, 4) } : {}),
  }
}

const sameCrop = (a: CropRect | null, b: CropRect) =>
  !a || (Math.abs(a.x - b.x) < 5e-4 && Math.abs(a.y - b.y) < 5e-4 && Math.abs(a.w - b.w) < 5e-4 && Math.abs(a.h - b.h) < 5e-4)

/**
 * Where a free crop starts and resets to: what the image shows with nothing
 * stored. Full width shows the whole photo; inline, a 816×400 band at 'center 60%'.
 */
function restCrop(target: FrameTarget, natural: number): CropRect {
  if (target !== 'inline') return { ...FULL_CROP }
  return frameToCrop({ natural, focus: { x: 0.5, y: INLINE_REST_Y }, zoom: 1, shape: INLINE_BOX })
}

/** The crop an existing frame shows (an inline frame from before free crop keeps its 816×400 band). */
function cropOf(target: FrameTarget, frame: ImageFrame | null, natural: number): CropRect {
  if (!frame) return restCrop(target, natural)
  if (target === 'inline' && frame.shape === undefined) return frameToCrop({ ...frame, natural, shape: INLINE_BOX })
  return frameToCrop({ ...frame, natural })
}

export function ImageEditor(props: ImageEditorProps) {
  // Mounting per open gives every session a fresh working frame, measured anew.
  if (!props.open) return null
  return <EditorDialog {...props} />
}

function EditorDialog({ src, frame, target, onClose, onDone }: ImageEditorProps) {
  const spec = TARGETS[target]
  const desktop = useMediaQuery('(min-width: 640px)')
  const titleId = useId()
  const hintId = useId()
  const stageRef = useRef<HTMLDivElement>(null)
  const [initial] = useState(() => safeFrame(frame))
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [problem, setProblem] = useState('')
  // The frame being edited, mirrored in a ref so a burst of pointer events
  // each builds on the last one without waiting for a render.
  const [work, setWorkState] = useState<ImageFrame | null>(initial)
  const workRef = useRef(work)
  const [dragging, setDragging] = useState(false)
  // Column images and wraps are cropped freely: this rectangle, not pan & zoom.
  const free = target === 'full' || target === 'inline' || target === 'wrap'
  const [crop, setCrop] = useState<CropRect | null>(null)
  const pointers = useRef(new Map<number, Point>())
  const pinch = useRef<{ dist: number; zoom: number } | null>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const cancel = useCallback(() => onCloseRef.current(), [])

  const setWork = useCallback((f: ImageFrame | null) => {
    workRef.current = f
    setWorkState(f)
  }, [])
  const commit = useCallback(
    (fn: (f: ImageFrame) => ImageFrame) => {
      if (workRef.current) setWork(fn(workRef.current))
    },
    [setWork],
  )

  /** The untouched framing: how the image sits with nothing stored. Only a full-width image's shape resets. */
  const rest = useCallback(
    (natural: number, shape?: number): ImageFrame => ({
      natural,
      focus: { x: 0.5, y: spec.restY },
      zoom: 1,
      ...(target !== 'full' && shape !== undefined ? { shape } : {}),
    }),
    [spec.restY, target],
  )
  const atRest = (f: ImageFrame) =>
    near(f.zoom, 1) && near(f.focus.x, 0.5) && near(f.focus.y, spec.restY) && (target !== 'full' || f.shape === undefined)

  const stageMetrics = () => {
    const r = stageRef.current?.getBoundingClientRect()
    return { left: r?.left ?? 0, top: r?.top ?? 0, box: { width: r?.width ?? 0, height: r?.height ?? 0 } }
  }

  // Declared before the focus effect below, so it sees what had focus before the editor.
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    return () => {
      if (previous?.isConnected) previous.focus({ preventScroll: true })
    }
  }, [])

  // The stage takes focus, so the arrow keys and +/− work straight away.
  useEffect(() => {
    stageRef.current?.focus({ preventScroll: true })
  }, [desktop])

  useEffect(() => {
    if (!desktop) return // MobileSheet handles Escape itself
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [desktop])

  // Native listeners on the stage: a wheel that can be cancelled (React's is
  // passive), Safari's trackpad pinch, and a failed image (error doesn't
  // bubble, but it does capture). A layout effect, so all are in place before
  // the image can finish loading.
  useLayoutEffect(() => {
    const el = stageRef.current
    if (!el) return
    const at = (r: DOMRect, x: number, y: number) => ({ x: x - r.left, y: y - r.top })
    const onWheel = (e: WheelEvent) => {
      if (!workRef.current || free) return
      e.preventDefault()
      const r = el.getBoundingClientRect()
      const px = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? r.height || 400 : 1)
      // Chrome and Firefox send a trackpad pinch as ctrl + wheel, in small steps.
      const k = e.ctrlKey ? 0.01 : 0.0015
      commit((f) => zoomIn(f, { width: r.width, height: r.height }, f.zoom * Math.exp(-px * k), at(r, e.clientX, e.clientY)))
    }
    // Safari sends a trackpad pinch as gesture events (touch pinches come through the pointer handlers).
    let gestureFrom = 1
    const onGesture = (e: Event) => {
      if (free) return
      e.preventDefault()
      if (!workRef.current || pointers.current.size > 0) return
      const g = e as Event & { scale?: number; clientX?: number; clientY?: number }
      if (e.type === 'gesturestart') {
        gestureFrom = workRef.current.zoom
        return
      }
      const r = el.getBoundingClientRect()
      const anchor = g.clientX !== undefined && g.clientY !== undefined ? at(r, g.clientX, g.clientY) : undefined
      commit((f) => zoomIn(f, { width: r.width, height: r.height }, gestureFrom * (g.scale ?? 1), anchor))
    }
    const onError = (e: Event) => {
      if (!(e.target instanceof HTMLImageElement)) return
      setProblem('This image couldn’t be loaded, so it can’t be cropped right now.')
      setStatus('error')
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    el.addEventListener('gesturestart', onGesture)
    el.addEventListener('gesturechange', onGesture)
    el.addEventListener('error', onError, true)
    return () => {
      el.removeEventListener('wheel', onWheel)
      el.removeEventListener('gesturestart', onGesture)
      el.removeEventListener('gesturechange', onGesture)
      el.removeEventListener('error', onError, true)
    }
  }, [desktop, commit, free])

  const measure = (img: HTMLImageElement) => {
    const { naturalWidth: w, naturalHeight: h } = img
    const natural = w / h
    if (!(w > 0 && h > 0)) {
      setProblem('This image couldn’t be loaded, so it can’t be cropped right now.')
      setStatus('error')
      return
    }
    // safeFrame drops anything outside this, so don't make a frame it would throw away.
    if (natural <= 0.05 || natural > 20) {
      setProblem('This image is too long and thin to crop.')
      setStatus('error')
      return
    }
    setStatus('ready')
    const f = workRef.current
    setWork(f ? { ...f, natural } : rest(natural))
    if (free) setCrop((c) => c ?? cropOf(target, initial, natural))
  }

  // ── Pointer: one finger or the mouse moves; two fingers pinch (and move) ──
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (!workRef.current || (e.pointerType === 'mouse' && e.button !== 0)) return
    e.preventDefault() // no text selection, no native image drag
    e.currentTarget.focus({ preventScroll: true })
    e.currentTarget.setPointerCapture?.(e.pointerId)
    const map = pointers.current
    map.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (map.size === 2) {
      const [a, b] = Array.from(map.values())
      pinch.current = { dist: Math.max(1, distance(a, b)), zoom: workRef.current.zoom }
    }
    setDragging(true)
  }

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const map = pointers.current
    const prev = map.get(e.pointerId)
    if (!prev) return
    const next = { x: e.clientX, y: e.clientY }
    const { left, top, box } = stageMetrics()
    const p = pinch.current
    if (map.size >= 2 && p) {
      const [a0, b0] = Array.from(map.values())
      map.set(e.pointerId, next)
      const [a1, b1] = Array.from(map.values())
      const m0 = midpoint(a0, b0)
      const m1 = midpoint(a1, b1)
      const zoom = (p.zoom * distance(a1, b1)) / p.dist
      // Follow the fingers' midpoint, then zoom about it.
      commit((f) => zoomIn(panBy(f, box, m1.x - m0.x, m1.y - m0.y), box, zoom, { x: m1.x - left, y: m1.y - top }))
      return
    }
    map.set(e.pointerId, next)
    commit((f) => panBy(f, box, next.x - prev.x, next.y - prev.y))
  }

  const onPointerEnd = (e: PointerEvent<HTMLDivElement>) => {
    const map = pointers.current
    if (!map.delete(e.pointerId)) return
    if (map.size < 2) pinch.current = null
    if (map.size === 0) setDragging(false)
  }

  // ── Keyboard and buttons ──
  const zoomStep = (dir: 1 | -1) =>
    commit((f) => {
      const z = f.zoom * (dir > 0 ? ZOOM_STEP : 1 / ZOOM_STEP)
      return zoomIn(f, stageMetrics().box, z < 1.005 ? 1 : z)
    })

  const onStageKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!workRef.current || e.altKey || e.ctrlKey || e.metaKey) return
    const step = e.shiftKey ? 48 : 8
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    }
    const move = moves[e.key]
    if (move) commit((f) => panBy(f, stageMetrics().box, move[0], move[1]))
    else if (e.key === '+' || e.key === '=') zoomStep(1)
    else if (e.key === '-' || e.key === '_') zoomStep(-1)
    else return
    e.preventDefault()
  }

  const reset = () => (free ? setCrop(restCrop(target, workRef.current?.natural ?? 1)) : commit((f) => rest(f.natural, f.shape)))

  const done = () => {
    const f = workRef.current
    if (!f || status === 'error') return
    if (free) {
      if (!crop || sameCrop(crop, restCrop(target, f.natural))) return onDone(null)
      // The whole photo is a real choice for an inline image (its default is a band), so it's kept as a shape.
      const framed = cropToFrame(crop, f.natural) ?? { natural: f.natural, focus: { x: 0.5, y: 0.5 }, zoom: 1, shape: f.natural }
      onDone(tidy(framed))
      return
    }
    onDone(atRest(f) && f.shape === undefined ? null : tidy(f))
  }

  // ── Render ──
  const ready = !!work && status !== 'error'
  const aspect = spec.aspect ?? work?.natural ?? 3 / 2
  const zoom = work?.zoom ?? 1
  const position = `center ${Math.round(spec.restY * 100)}%`
  const radius = spec.scene ? '' : 'rounded-2xl'
  const shade = spec.scene ? <div aria-hidden className={`pointer-events-none absolute inset-0 ${SCENE_SHADE}`} /> : null
  const maxH = desktop ? STAGE_MAX_H.desktop : STAGE_MAX_H.phone

  const header = (
    <div className="flex flex-col gap-1">
      <span className={labelCls}>{spec.label}</span>
      <h2 id={titleId} className="m-0 font-display text-[22px]/[1.2] text-primary">
        Reposition
      </h2>
      {free ? (
        <>
          <p className="m-0 text-[13px] text-muted-foreground">Drag a corner or an edge to crop · drag inside to move</p>
          <p id={hintId} className="sr-only">
            Drag the crop box's corners or edges to crop to any shape, or drag inside it to move it. With the keyboard, the arrow
            keys move the crop box, and Shift with the arrow keys makes it bigger or smaller.
          </p>
        </>
      ) : (
        <>
          <p className="m-0 text-[13px] text-muted-foreground">Drag to move · {desktop ? 'scroll' : 'pinch'} or slide to zoom</p>
          <p id={hintId} className="sr-only">
            Drag the image to move it; scroll, pinch or use the zoom slider to zoom. With the keyboard, the arrow keys move it (hold
            Shift for bigger steps), and plus and minus zoom in and out.
          </p>
        </>
      )}
    </div>
  )

  const freeStage = free && status !== 'error' && (
    <CropBox
      ref={stageRef}
      src={src}
      natural={work?.natural ?? null}
      rect={crop}
      onChange={setCrop}
      onLoad={measure}
      maxHeight={maxH}
      maxWidth={STAGE_MAX_W}
      describedBy={hintId}
    />
  )

  const stage = (
    <div className="flex justify-center">
      <div
        ref={stageRef}
        role="application"
        aria-label="Crop area"
        aria-describedby={hintId}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onLostPointerCapture={onPointerEnd}
        onKeyDown={onStageKey}
        style={{ aspectRatio: String(aspect), width: `min(100%, ${STAGE_MAX_W}px, calc(${maxH} * ${aspect}))` }}
        className={`relative touch-none select-none overflow-hidden bg-foreground/8 shadow-[inset_0_0_0_1px_rgba(45,27,30,.1)] ${radius} ${
          ready ? (dragging ? 'cursor-grabbing' : 'cursor-grab') : ''
        }`}
      >
        {status !== 'error' && (
          <FramedImage
            src={src}
            frame={work}
            defaultPosition={position}
            draggable={false}
            onLoad={measure}
            style={{ position: 'absolute', inset: 0 }}
            // Hidden until measured, so a full-width image never shows in the placeholder shape.
            className={`pointer-events-none ${work ? '' : 'opacity-0'}`}
          >
            {shade}
          </FramedImage>
        )}
        <Thirds show={dragging} />
        {status === 'error' ? (
          <p role="alert" className="absolute inset-0 m-0 flex items-center justify-center px-6 text-center text-[13px]/[1.5] text-muted-foreground">
            {problem}
          </p>
        ) : (
          !work && (
            <p className="pointer-events-none absolute inset-0 m-0 flex items-center justify-center font-display text-[14px] italic text-muted-foreground">
              Loading image…
            </p>
          )
        )}
      </div>
    </div>
  )

  const zoomRow = (
    <div className="flex items-center gap-2">
      <RoundButton label="Zoom out" disabled={!ready || zoom <= MIN_ZOOM} onClick={() => zoomStep(-1)}>
        −
      </RoundButton>
      <input
        type="range"
        min={MIN_ZOOM}
        max={MAX_ZOOM}
        step={0.01}
        value={zoom}
        disabled={!ready}
        aria-label="Zoom"
        aria-valuetext={`${Math.round(zoom * 100)}%`}
        onChange={(e) => {
          const z = Number(e.target.value)
          commit((f) => zoomIn(f, stageMetrics().box, z))
        }}
        className="h-1 min-w-0 flex-1 cursor-pointer accent-wine disabled:cursor-not-allowed disabled:opacity-50"
      />
      <RoundButton label="Zoom in" disabled={!ready || zoom >= MAX_ZOOM} onClick={() => zoomStep(1)}>
        +
      </RoundButton>
      <span aria-hidden className="w-11 text-right text-[12px] font-medium tabular-nums text-muted-foreground">
        {Math.round(zoom * 100)}%
      </span>
    </div>
  )

  const previewBox = desktop ? PREVIEW_BOX.desktop : PREVIEW_BOX.phone
  const previews = spec.previews.length > 0 && (
    <div className="flex flex-col gap-2">
      <span className={labelCls}>PREVIEW</span>
      <div className="-mx-1 flex items-end gap-4 overflow-x-auto px-1 pb-1">
        {spec.previews.map((p) => {
          const a = p.w / p.h
          const height = Math.min(previewBox.h, previewBox.w / a)
          return (
            <figure key={p.label} className="m-0 flex shrink-0 flex-col gap-1.5">
              <FramedImage
                src={src}
                frame={work}
                defaultPosition={position}
                draggable={false}
                style={{ width: Math.round(height * a), height: Math.round(height) }}
                className={`bg-foreground/8 ${spec.scene ? 'rounded-[2px]' : 'rounded-[4px]'}`}
              >
                {shade}
              </FramedImage>
              <figcaption className="text-[11px] font-medium text-muted-foreground">{p.label}</figcaption>
            </figure>
          )
        })}
      </div>
    </div>
  )

  const footer = (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={reset}
        disabled={!ready || (free ? sameCrop(crop, restCrop(target, work?.natural ?? 1)) : atRest(work))}
        className={`${btn.ghost} disabled:cursor-not-allowed disabled:opacity-40`}
      >
        Reset
      </button>
      {target === 'inline' && (
        // One step to the photo's own shape (the band is the default); what a full-width image shows.
        <button
          type="button"
          onClick={() => setCrop({ ...FULL_CROP })}
          disabled={!ready || sameCrop(crop, FULL_CROP)}
          className={`${btn.ghost} disabled:cursor-not-allowed disabled:opacity-40`}
        >
          Whole photo
        </button>
      )}
      <span className="flex-1" />
      <button type="button" onClick={cancel} className={btn.outline}>
        Cancel
      </button>
      <button type="button" onClick={done} disabled={!ready} className={btn.primary}>
        Done
      </button>
    </div>
  )

  const body = (
    <>
      {header}
      {free && status !== 'error' ? freeStage : stage}
      {!free && zoomRow}
      {previews}
    </>
  )

  // data-keep-selection: the composer keeps the block selected while its image is framed.
  if (!desktop) {
    return (
      <div data-keep-selection>
        <MobileSheet label="Reposition" onClose={cancel}>
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 pb-4 pt-3 font-sans">{body}</div>
          <div className="shrink-0 border-t border-border/40 px-5 py-3 font-sans">{footer}</div>
        </MobileSheet>
      </div>
    )
  }
  return (
    <div data-keep-selection className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
      <button type="button" aria-label="Close" tabIndex={-1} onClick={cancel} className="absolute inset-0 bg-foreground/32" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative flex max-h-full w-full max-w-[768px] flex-col gap-4 overflow-y-auto rounded-2xl bg-card p-6 font-sans shadow-[var(--shadow-sheet)]"
      >
        {body}
        {footer}
      </div>
    </div>
  )
}

/** Rule-of-thirds lines, shown while the image is being moved. */
function Thirds({ show }: { show: boolean }) {
  const line = 'absolute bg-card/75 shadow-[0_0_2px_rgba(45,27,30,.5)]'
  return (
    <div aria-hidden className={`pointer-events-none absolute inset-0 transition-opacity duration-150 ${show ? 'opacity-100' : 'opacity-0'}`}>
      <span className={`${line} inset-y-0 left-1/3 w-px`} />
      <span className={`${line} inset-y-0 left-2/3 w-px`} />
      <span className={`${line} inset-x-0 top-1/3 h-px`} />
      <span className={`${line} inset-x-0 top-2/3 h-px`} />
    </div>
  )
}

function RoundButton({ label, disabled, onClick, children }: { label: string; disabled?: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-8 shrink-0 items-center justify-center rounded-full text-[17px] leading-none text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-35"
    >
      {children}
    </button>
  )
}
